import {useState} from 'react'
import {type ApiErrorT} from '@frontrow/console/src/entities/models/api-error'
import {type NonEmptyArray} from '@freckle/non-empty'
import {type OperationLocationT} from '@frontrow/console/src/entities/helpers/operation'
import {type GroupedArtifactData} from '@frontrow/console/src/entities/models/artifact'
import {
  type StudentLevelInfoT,
  type StudentLevelErrorT
} from '@frontrow/console/src/models/student-level-info'
import {
  updateStudentMathLevelsUsingCsvFile,
  updateStudentElaLevelsUsingCsvFile,
  updateStudentLevelsUsingFastBridgeCsvFile,
  updateStudentLevelsUsingNgmtCsvFile,
  updateStudentLevelsUsingMAPCsvFile
} from '@frontrow/console/src/helpers/api-students'
import {fromJust} from '@freckle/maybe'
import {exhaustive} from '@freckle/exhaustive'
import {type ContentAreaT, ContentAreas} from '@frontrow/console/src/entities/helpers/content-area'
import {responseApiErrors} from '@frontrow/console/src/entities/models/api-error'

export type CsvUploadStatusT =
  | 'sending-file-for-preview'
  | 'preview-parsing-success'
  | 'parsing-failure'
  | 'no-preview-available'
  | 'sending-file-for-update'
  | 'running-operation'
  | 'update-success'
  | 'update-failure'

export type CsvUploadLevelsT = 'Math' | 'Ela'

export type CsvUploadAssessmentT =
  | {
      tag: 'Old MAP'
    }
  | {
      tag: 'MAP'
    }
  | {
      tag: 'FastBridge'
      scoreColumnName: string | undefined | null
    }
  | {
      tag: 'Ngmt'
    }

type StudentsCsvUploadStateT = {
  parsedStudentLevelCsvData: StudentLevelInfoT | undefined | null
  csvPreviewParsingError: NonEmptyArray<ApiErrorT> | undefined | null
  warnings: Array<StudentLevelErrorT> | undefined | null
  csvUpdateError: string | undefined | null
  updateStatus: CsvUploadStatusT | undefined | null
  operationLocation: OperationLocationT | undefined | null
  artifactData: GroupedArtifactData | undefined | null
}

const uploadLevelsTypeToContentArea = (type: CsvUploadLevelsT): ContentAreaT => {
  switch (type) {
    case 'Math':
      return ContentAreas.math
    case 'Ela':
      return ContentAreas.ela
    default:
      return exhaustive(type)
  }
}

export function useStudentCsvUpload() {
  const [state, setState] = useState<StudentsCsvUploadStateT>({
    parsedStudentLevelCsvData: null,
    csvPreviewParsingError: null,
    warnings: null,
    csvUpdateError: null,
    updateStatus: null,
    operationLocation: null,
    artifactData: null
  })

  const previewCsvFile = (
    _districtId: number,
    _file: File,
    type: CsvUploadLevelsT,
    assessmentType: CsvUploadAssessmentT
  ): void => {
    switch (assessmentType.tag) {
      case 'Old MAP': {
        switch (type) {
          case 'Math':
          case 'Ela':
            setState(prev => ({
              ...prev,
              updateStatus: 'no-preview-available'
            }))
            return
          default:
            return exhaustive(type)
        }
      }
      case 'MAP':
      case 'FastBridge':
      case 'Ngmt':
        setState(prev => ({
          ...prev,
          updateStatus: 'no-preview-available'
        }))
        return
      default:
        return exhaustive(assessmentType as never)
    }
  }

  const clearCsvPreview = (): void => {
    setState(prev => ({
      ...prev,
      updateStatus: null,
      parsedStudentLevelCsvData: null,
      csvPreviewParsingError: null
    }))
  }

  const updateStudentLevelsCsvResponse = (artifactData: GroupedArtifactData): void => {
    setState(prev => ({
      ...prev,
      updateStatus: 'update-success',
      csvUpdateError: null,
      operationLocation: null,
      artifactData
    }))
  }

  const updateStudentLevelsCsvError = (error: string): void => {
    setState(prev => ({
      ...prev,
      updateStatus: 'update-failure',
      csvUpdateError: error
    }))
  }

  const operationRunning = (operationLocation: OperationLocationT): void => {
    setState(prev => ({
      ...prev,
      updateStatus: 'running-operation',
      operationLocation
    }))
  }

  const csvFileError = (error: NonEmptyArray<ApiErrorT>): void => {
    setState(prev => ({
      ...prev,
      updateStatus: 'parsing-failure',
      parsedStudentLevelCsvData: null,
      csvPreviewParsingError: error
    }))
  }

  const updateStudentLevels = (
    districtId: number,
    file: File,
    type: CsvUploadLevelsT,
    assessmentType: CsvUploadAssessmentT,
    overrideRecentPractice: boolean
  ): void => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const handleError = (error: any) => {
      if (error.status === 400) {
        csvFileError(responseApiErrors(error))
      } else if (error.stats === 401 || error.status === 403) {
        updateStudentLevelsCsvError('You are not authorized to update student levels.')
      } else {
        updateStudentLevelsCsvError(error.message)
      }
    }

    setState(prev => ({
      ...prev,
      updateStatus: 'sending-file-for-update',
      csvUpdateError: null
    }))

    switch (assessmentType.tag) {
      case 'Old MAP': {
        switch (type) {
          case 'Math':
            updateStudentMathLevelsUsingCsvFile(districtId, file)
              .then(operationLocation => {
                operationRunning(operationLocation)
              })
              .catch(handleError)
            return
          case 'Ela':
            updateStudentElaLevelsUsingCsvFile(districtId, file)
              .then(operationLocation => {
                operationRunning(operationLocation)
              })
              .catch(handleError)
            return
          default:
            return exhaustive(type)
        }
      }
      case 'FastBridge': {
        const scoreColumnName = fromJust(
          assessmentType.scoreColumnName,
          'Score column name is null'
        )
        const contentArea = uploadLevelsTypeToContentArea(type)

        updateStudentLevelsUsingFastBridgeCsvFile(
          districtId,
          contentArea,
          file,
          scoreColumnName,
          overrideRecentPractice
        )
          .then(operationLocation => {
            operationRunning(operationLocation)
          })
          .catch(handleError)
        return
      }
      case 'Ngmt':
        updateStudentLevelsUsingNgmtCsvFile(districtId, file, overrideRecentPractice)
          .then(operationLocation => {
            operationRunning(operationLocation)
          })
          .catch(handleError)
        return
      case 'MAP': {
        const contentArea = uploadLevelsTypeToContentArea(type)

        updateStudentLevelsUsingMAPCsvFile(districtId, contentArea, file, overrideRecentPractice)
          .then(operationLocation => {
            operationRunning(operationLocation)
          })
          .catch(handleError)
        return
      }
      default:
        return exhaustive(assessmentType as never)
    }
  }

  return {
    ...state,
    previewCsvFile,
    clearCsvPreview,
    updateStudentLevels,
    updateStudentLevelsCsvResponse,
    updateStudentLevelsCsvError
  }
}
